import { Lock } from 'lucide-react'
import React, { Suspense } from 'react'

import { useReferralById } from 'src/features/business/referrals/api/referrals/use-referral-by-id'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { Separator } from 'src/shared/components/ui/miscellaneous/separator'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import type { ReferralShareStatus } from 'src/shared/schemas/referrals'
import { allReferralFields, referralFields, referralInfoByProfession } from 'src/shared/schemas/referrals'
import { cn } from 'src/shared/utils/cn'
import { formatFieldValue } from 'src/shared/utils/format-field-value'

interface ReferralDetailsProps {
  referralId: number
  status: ReferralShareStatus
  profession?: Profession
}

export const renderSkeleton = (text: string) => {
  return text
    .split(' ')
    .slice(0, 5)
    .map((word, index) => (
      <Skeleton
        key={index}
        className="inline-block h-4"
        style={{ width: `${Math.min(word.length * 18, 125)}px`, marginRight: '5px' }}
      />
    ))
}

//TODO: (fix) format field data based on type of data
export const ReferralDetailsContent: React.FC<ReferralDetailsProps> = ({ referralId, status, profession }) => {
  const { data: referral } = useReferralById(Number(referralId))
  if (!referral) {
    return <div>It&apos;s quiet in here...</div>
  }

  const fieldValues = profession ? referralInfoByProfession[profession] : allReferralFields

  return (
    <div className="space-y-4">
      {fieldValues.map((fieldValue) => {
        const fieldValueData = referral[fieldValue]
        if (
          (!fieldValueData && fieldValueData !== 0) ||
          (Array.isArray(fieldValueData) && fieldValueData.length === 0)
        ) {
          return null
        }

        const fieldType = referralFields[fieldValue].type
        const isFullWidth = ['text', 'list', 'priceRange'].includes(fieldType)

        return (
          <div key={fieldValue}>
            <div className={cn('flex flex-wrap items-center justify-between gap-2 py-2')}>
              <div className="flex flex-row items-center justify-between">
                {React.createElement(referralFields[fieldValue].icon, { className: 'mr-3 text-muted' })}
                <div className="flex-1 font-medium text-primary">{referralFields[fieldValue].label}</div>
              </div>
              {referralFields[fieldValue].requiresApproval ? (
                status === 'approved' ? (
                  <div className={cn('text-muted')}>
                    {formatFieldValue(referralFields[fieldValue], fieldValueData, referral)}
                  </div>
                ) : (
                  <div className={cn('flex items-center text-muted', !isFullWidth && 'ml-auto')}>
                    <Lock className="mr-1 size-4" />
                    {renderSkeleton(String(fieldValue))}
                  </div>
                )
              ) : (
                <div className={cn('text-muted')}>
                  {formatFieldValue(referralFields[fieldValue], fieldValueData, referral)}
                </div>
              )}
            </div>
            <Separator />
          </div>
        )
      })}
    </div>
  )
}

const ReferralDetailsSkeleton = () => {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  )
}

export const ReferralDetails: React.FC<ReferralDetailsProps> = ({ referralId, status, profession }) => {
  return (
    <ErrorBoundaryWrapper
      title="Sorry, there was an error loading the details for this referral"
      description="Please try again later"
    >
      <Suspense fallback={<ReferralDetailsSkeleton />}>
        <ReferralDetailsContent referralId={referralId} status={status} profession={profession} />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}
